// The screen-space passes of the original's scene script (CommonData/Scripts/SceneScript.xss) and
// their shaders (assets/shaders/glsl, Root.Effects.*), as PostProcessor and WS_WaterGeomManager
// set them up (docs/platform/RenderFidelity.md "Render passes"):
// - `DRP_TransBG` renders the skybox into the half-size `DRS_BG` (cleared to 0, own depth);
// - `Pass_PP_BlurV` / `Pass_PP_BlurH` (`PostProcessor::BackgroundBlur_Enable`, on by default:
//   `GameApp::m_bBackgroundBlur`) blur it into `DRS_BlurV` then `DRS_BlurH` with
//   `PP_BlurVertical/HorizontalF` at `PP_BlurSize` (GameOptions.txt: 1.2);
// - `FishTankPass:BG_Copy_Layer` (`UpdateDrawFishTankBackground` 0xe40290) copies `DRS_BlurH`
//   over the whole main colour;
// - `WaterReflectionPass{Back,Front}:WaterReflectionLayer` (`UpdateWaterReflection` 0xe40490)
//   copy the main colour into the half-size `WaterReflection` the water shader samples;
// - `PostProcessA` with `kAA_Fxaa` (the default: `AntiAliasing::SetAntiAliasingMode` 0xd52a60
//   disables `AAPrePass`, enables `AAPass` on `DRC_Final` and points `PostProcessA` at
//   `AAInputCanvas`): `CombineLayers` (`Root.System.FinalPass`, a copy of the main colour) and
//   `ParticlesAdditive` into `AASurfaceInputCol`, then `AAPass` (`Root.Effects.FXAAF`) back into
//   the main colour;
// - bloom (`PostProcessor::BloomEnable`, on by default): `PP_BloomLumF` of the main colour into
//   `DRS_BlurH` (the theme's `BloomDefaults` threshold and intensity, Themes.txt), the two blurs
//   at the theme's blur size, `PP_BloomCombine` (main + bloom) into `DRS_Background`;
// - `EffectsPass:EffectsLayer` (`PP_SepiaF`): the effect modes, sepia and tint colours and the
//   vignette intensity of `PostProcessor`'s animators (postfx.ts; the vignette starts at the
//   theme's `VignetteIntensity`) with the alpha of `WorldSystem/Vignette`, onto the screen.
// All targets are RGBA8 and hold the raw (non-linearised) colour values, like the original's
// GL surfaces: the client renders without colour-space conversion (renderer.ts).
import * as THREE from 'three';
import type { EffectUniforms } from './postfx.ts';

const QUAD_VERT = /* glsl */ `
in vec3 position;
in vec2 uv;
out vec2 INUV;
void main() {
  INUV = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}`;

/** `Root.Effects.PP_BlurVerticalF` / `PP_BlurHorizontalF` (the horizontal one weighs the centre
 * 0.188, the vertical one 0.18; both offset by half a texel of the half-size height). */
function blurFrag(vertical: boolean): string {
  const taps = [[-4, 0.05], [-3, 0.09], [-2, 0.12], [-1, 0.15], [1, 0.15], [2, 0.12], [3, 0.09], [4, 0.05]]
    .map(([k, w]) => {
      const off = `(pix.${vertical ? 'y' : 'x'} * (${k.toFixed(1)} * uBlurSize)) ${k < 0 ? '+' : '-'} halfpix`;
      return `  colour += texture(Screen, INUV + vec2(${vertical ? `0.0, ${off}` : `${off}, 0.0`})) * ${w};`;
    }).join('\n');
  return /* glsl */ `
precision highp float;
in vec2 INUV;
uniform sampler2D Screen;
uniform vec4 uScreenRes;
uniform float uBlurSize;
out vec4 OUTColour;
void main() {
  float sWidth = uScreenRes.x * 0.5;
  float sHeight = uScreenRes.y * 0.5;
  float halfpix = 0.5 / sHeight;
  vec2 pix = vec2(1.0 / sWidth, 1.0 / sHeight);
  vec4 colour = vec4(0.0, 0.0, 0.0, 1.0);
${taps}
  colour += texture(Screen, INUV) * ${vertical ? '0.18' : '0.188'};
  colour.a = 1.0;
  OUTColour = colour;
}`;
}

/** `XPrimitiveSystem::DrawTexturedQuad` of a whole surface (the BG copy and the reflection copy):
 * the texture as it is, no blending. */
const COPY_FRAG = /* glsl */ `
precision highp float;
in vec2 INUV;
uniform sampler2D Screen;
out vec4 OUTColour;
void main() { OUTColour = texture(Screen, INUV); }`;

/** `Root.Effects.PP_BloomLumF`. */
const LUM_FRAG = /* glsl */ `
precision highp float;
in vec2 INUV;
uniform sampler2D sScreen;
uniform float lumThreshold;
uniform float lumMulti;
out vec4 OUTColour;
void main() {
  vec4 pixel = texture(sScreen, INUV);
  float luminance = dot(pixel.xyz, vec3(0.299, 0.587, 0.114));
  if (luminance >= lumThreshold) luminance *= lumMulti;
  else luminance = 0.0;
  OUTColour = vec4(luminance, luminance, luminance, 1.0);
}`;

/** `Root.Effects.PP_BloomCombine`. */
const COMBINE_FRAG = /* glsl */ `
precision highp float;
in vec2 INUV;
uniform sampler2D sScreen;
uniform sampler2D sBloom;
out vec4 OUTColour;
void main() { OUTColour = texture(sScreen, INUV) + texture(sBloom, INUV); }`;

/** `Root.Effects.FXAAF`: FXAA 3.11 Quality (`FXAA_PC`, `FXAA_QUALITY__PRESET` 39, subpix 0.75,
 * edge threshold 0.166, edge threshold min 0.0433) with `FXAA_GREEN_AS_LUMA` 0: the luma is the
 * input's alpha channel. With `AAPrePass` disabled nothing writes luma there, so the edges FXAA
 * finds are the edges of the main colour's alpha. The output keeps the centre's alpha. The
 * search steps of preset 39 are the unrolled `FXAA_QUALITY__P0`..`P11`. The texture reads are
 * the `FXAA_GATHER4_ALPHA` 0 path (single fetches; the gather path reads the same texels). */
const FXAA_FRAG = /* glsl */ `
precision highp float;
in vec2 INUV;
uniform sampler2D texture0;
uniform vec2 rcpFrame;
out vec4 OUTColour;
const float SUBPIX = 0.75;
const float EDGE_THRESHOLD = 0.166;
const float EDGE_THRESHOLD_MIN = 0.0433;
const float P[12] = float[12](1.0, 1.0, 1.0, 1.0, 1.0, 1.5, 2.0, 2.0, 2.0, 2.0, 4.0, 8.0);
float lumaAt(vec2 p) { return textureLod(texture0, p, 0.0).w; }
void main() {
  vec2 posM = INUV;
  vec4 rgbyM = textureLod(texture0, posM, 0.0);
  float lumaM = rgbyM.w;
  float lumaS = textureLodOffset(texture0, posM, 0.0, ivec2(0, 1)).w;
  float lumaE = textureLodOffset(texture0, posM, 0.0, ivec2(1, 0)).w;
  float lumaN = textureLodOffset(texture0, posM, 0.0, ivec2(0, -1)).w;
  float lumaW = textureLodOffset(texture0, posM, 0.0, ivec2(-1, 0)).w;
  float maxSM = max(lumaS, lumaM);
  float minSM = min(lumaS, lumaM);
  float maxESM = max(lumaE, maxSM);
  float minESM = min(lumaE, minSM);
  float maxWN = max(lumaN, lumaW);
  float minWN = min(lumaN, lumaW);
  float rangeMax = max(maxWN, maxESM);
  float rangeMin = min(minWN, minESM);
  float rangeMaxScaled = rangeMax * EDGE_THRESHOLD;
  float range = rangeMax - rangeMin;
  float rangeMaxClamped = max(EDGE_THRESHOLD_MIN, rangeMaxScaled);
  if (range < rangeMaxClamped) { OUTColour = rgbyM; return; }
  float lumaNW = textureLodOffset(texture0, posM, 0.0, ivec2(-1, -1)).w;
  float lumaSE = textureLodOffset(texture0, posM, 0.0, ivec2(1, 1)).w;
  float lumaNE = textureLodOffset(texture0, posM, 0.0, ivec2(1, -1)).w;
  float lumaSW = textureLodOffset(texture0, posM, 0.0, ivec2(-1, 1)).w;
  float lumaNS = lumaN + lumaS;
  float lumaWE = lumaW + lumaE;
  float subpixRcpRange = 1.0 / range;
  float subpixNSWE = lumaNS + lumaWE;
  float edgeHorz1 = (-2.0 * lumaM) + lumaNS;
  float edgeVert1 = (-2.0 * lumaM) + lumaWE;
  float lumaNESE = lumaNE + lumaSE;
  float lumaNWNE = lumaNW + lumaNE;
  float edgeHorz2 = (-2.0 * lumaE) + lumaNESE;
  float edgeVert2 = (-2.0 * lumaN) + lumaNWNE;
  float lumaNWSW = lumaNW + lumaSW;
  float lumaSWSE = lumaSW + lumaSE;
  float edgeHorz4 = (abs(edgeHorz1) * 2.0) + abs(edgeHorz2);
  float edgeVert4 = (abs(edgeVert1) * 2.0) + abs(edgeVert2);
  float edgeHorz3 = (-2.0 * lumaW) + lumaNWSW;
  float edgeVert3 = (-2.0 * lumaS) + lumaSWSE;
  float edgeHorz = abs(edgeHorz3) + edgeHorz4;
  float edgeVert = abs(edgeVert3) + edgeVert4;
  float subpixNWSWNESE = lumaNWSW + lumaNESE;
  float lengthSign = rcpFrame.x;
  bool horzSpan = edgeHorz >= edgeVert;
  float subpixA = subpixNSWE * 2.0 + subpixNWSWNESE;
  if (!horzSpan) lumaN = lumaW;
  if (!horzSpan) lumaS = lumaE;
  if (horzSpan) lengthSign = rcpFrame.y;
  float subpixB = (subpixA * (1.0 / 12.0)) - lumaM;
  float gradientN = lumaN - lumaM;
  float gradientS = lumaS - lumaM;
  float lumaNN = lumaN + lumaM;
  float lumaSS = lumaS + lumaM;
  bool pairN = abs(gradientN) >= abs(gradientS);
  float gradient = max(abs(gradientN), abs(gradientS));
  if (pairN) lengthSign = -lengthSign;
  float subpixC = clamp(abs(subpixB) * subpixRcpRange, 0.0, 1.0);
  vec2 posB = posM;
  vec2 offNP = vec2(!horzSpan ? 0.0 : rcpFrame.x, horzSpan ? 0.0 : rcpFrame.y);
  if (!horzSpan) posB.x += lengthSign * 0.5;
  if (horzSpan) posB.y += lengthSign * 0.5;
  vec2 posN = posB - offNP * P[0];
  vec2 posP = posB + offNP * P[0];
  float subpixD = ((-2.0) * subpixC) + 3.0;
  float lumaEndN = lumaAt(posN);
  float subpixE = subpixC * subpixC;
  float lumaEndP = lumaAt(posP);
  if (!pairN) lumaNN = lumaSS;
  float gradientScaled = gradient * 1.0 / 4.0;
  float lumaMM = lumaM - lumaNN * 0.5;
  float subpixF = subpixD * subpixE;
  bool lumaMLTZero = lumaMM < 0.0;
  lumaEndN -= lumaNN * 0.5;
  lumaEndP -= lumaNN * 0.5;
  bool doneN = abs(lumaEndN) >= gradientScaled;
  bool doneP = abs(lumaEndP) >= gradientScaled;
  if (!doneN) posN -= offNP * P[1];
  bool doneNP = (!doneN) || (!doneP);
  if (!doneP) posP += offNP * P[1];
  for (int i = 2; i < 12; i++) {
    if (!doneNP) break;
    if (!doneN) lumaEndN = lumaAt(posN) - lumaNN * 0.5;
    if (!doneP) lumaEndP = lumaAt(posP) - lumaNN * 0.5;
    doneN = abs(lumaEndN) >= gradientScaled;
    doneP = abs(lumaEndP) >= gradientScaled;
    if (!doneN) posN -= offNP * P[i];
    doneNP = (!doneN) || (!doneP);
    if (!doneP) posP += offNP * P[i];
  }
  float dstN = posM.x - posN.x;
  float dstP = posP.x - posM.x;
  if (!horzSpan) dstN = posM.y - posN.y;
  if (!horzSpan) dstP = posP.y - posM.y;
  bool goodSpanN = (lumaEndN < 0.0) != lumaMLTZero;
  float spanLength = (dstP + dstN);
  bool goodSpanP = (lumaEndP < 0.0) != lumaMLTZero;
  float spanLengthRcp = 1.0 / spanLength;
  bool directionN = dstN < dstP;
  float dst = min(dstN, dstP);
  bool goodSpan = directionN ? goodSpanN : goodSpanP;
  float subpixG = subpixF * subpixF;
  float pixelOffset = (dst * (-spanLengthRcp)) + 0.5;
  float subpixH = subpixG * SUBPIX;
  float pixelOffsetGood = goodSpan ? pixelOffset : 0.0;
  float pixelOffsetSubpix = max(pixelOffsetGood, subpixH);
  if (!horzSpan) posM.x += pixelOffsetSubpix * lengthSign;
  if (horzSpan) posM.y += pixelOffsetSubpix * lengthSign;
  OUTColour = vec4(textureLod(texture0, posM, 0.0).xyz, lumaM);
}`;

/** `Root.Effects.PP_SepiaF` (invert, sepia, tint, vivid light, vignette). */
const EFFECTS_FRAG = /* glsl */ `
precision highp float;
in vec2 INUV;
uniform sampler2D sScreen;
uniform sampler2D sVignette;
uniform vec4 fSepiaColour;
uniform vec4 fTintColour;
uniform float fVignetteAlpha;
uniform vec4 fEffectMode;
out vec4 OUTColour;
float burn(float base, float blend) { return blend == 0.0 ? blend : max(1.0 - ((1.0 - base) / blend), 0.0); }
float dodge(float base, float blend) { return blend == 1.0 ? blend : min(base / (1.0 - blend), 1.0); }
float vivid(float base, float blend) { return blend < 0.5 ? burn(base, 2.0 * blend) : dodge(base, 2.0 * (blend - 0.5)); }
void main() {
  vec4 finalColour = texture(sScreen, INUV);
  if (fEffectMode.z > 0.0) finalColour.rgb = mix(finalColour.rgb, vec3(1.0) - finalColour.rgb, fEffectMode.z);
  if (fEffectMode.x > 0.0) {
    float luminance = dot(finalColour.xyz, vec3(0.3, 0.4, 0.3));
    finalColour.rgb = mix(finalColour.rgb, fSepiaColour.rgb * luminance, fEffectMode.x);
  }
  if (fEffectMode.y > 0.0) finalColour.rgb = mix(finalColour.rgb, clamp(finalColour.rgb + fTintColour.rgb, 0.0, 1.0), fEffectMode.y);
  if (fEffectMode.w > 0.0) {
    vec3 c = finalColour.rgb;
    vec3 v = vec3(vivid(0.25, c.r), vivid(0.25, c.g), vivid(0.25, c.b));
    finalColour.rgb = mix(finalColour.rgb, v, fEffectMode.w);
  }
  if (fVignetteAlpha > 0.0) {
    vec4 vignette = texture(sVignette, INUV);
    finalColour.rgb *= (1.0 - (vignette.a * fVignetteAlpha));
  }
  OUTColour = vec4(finalColour.rgb, 1.0);
}`;

type Target = THREE.WebGLRenderTarget;

function target(w: number, h: number, depth: boolean): Target {
  return new THREE.WebGLRenderTarget(w, h, {
    depthBuffer: depth, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    generateMipmaps: false, type: THREE.UnsignedByteType, format: THREE.RGBAFormat, colorSpace: THREE.NoColorSpace,
  });
}

function pass(frag: string, uniforms: Record<string, THREE.IUniform>): THREE.RawShaderMaterial {
  return new THREE.RawShaderMaterial({
    glslVersion: THREE.GLSL3, vertexShader: QUAD_VERT, fragmentShader: frag, uniforms,
    depthTest: false, depthWrite: false, blending: THREE.NoBlending, side: THREE.DoubleSide,
  });
}

/** The theme's post-processing values (Themes.txt `BloomDefaults`, `VignetteIntensity`). */
export type PostTheme = { bloomThreshold: number; bloomIntensity: number; bloomBlurSize: number; vignette: number };

/** China's (the reference theme of the recorded matches) until the theme's values loaded. */
export const DEFAULT_POST: PostTheme = { bloomThreshold: 0.88, bloomIntensity: 0.5835, bloomBlurSize: 2.0, vignette: 0.5 };

/** `PP_BlurSize` (GameOptions.txt): the background blur's size. */
export const BG_BLUR_SIZE = 1.2;

export class PostChain {
  /** `DRS_BG` + `DRS_BG_Depth` (50 %). */
  bg: Target;
  /** `DRS_BlurV`, `DRS_BlurH` (50 %). */
  blurV: Target;
  blurH: Target;
  /** `DRS_MainColour` + `DRS_Depth`. */
  main: Target;
  /** `WaterReflection` (50 %). */
  reflection: Target;
  /** `AASurfaceInputCol` (`AAInputCanvas`: `PostProcessA`'s target with FXAA on). */
  aaInput: Target;
  /** `DRS_Background` (the bloom composite). */
  background: Target;
  theme: PostTheme = DEFAULT_POST;
  private quad: THREE.Mesh;
  private scene = new THREE.Scene();
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private blurVMat: THREE.RawShaderMaterial;
  private blurHMat: THREE.RawShaderMaterial;
  private copyMat: THREE.RawShaderMaterial;
  private lumMat: THREE.RawShaderMaterial;
  private combineMat: THREE.RawShaderMaterial;
  private fxaaMat: THREE.RawShaderMaterial;
  private effectsMat: THREE.RawShaderMaterial;
  private width = 1;
  private height = 1;

  constructor() {
    this.bg = target(1, 1, true);
    this.blurV = target(1, 1, false);
    this.blurH = target(1, 1, false);
    this.main = target(1, 1, true);
    this.reflection = target(1, 1, false);
    this.aaInput = target(1, 1, false);
    this.background = target(1, 1, false);
    const res = { value: new THREE.Vector4(1, 1, 0, 0) };
    this.blurVMat = pass(blurFrag(true), { Screen: { value: null }, uScreenRes: res, uBlurSize: { value: BG_BLUR_SIZE } });
    this.blurHMat = pass(blurFrag(false), { Screen: { value: null }, uScreenRes: res, uBlurSize: { value: BG_BLUR_SIZE } });
    this.copyMat = pass(COPY_FRAG, { Screen: { value: null } });
    this.lumMat = pass(LUM_FRAG, { sScreen: { value: null }, lumThreshold: { value: 0 }, lumMulti: { value: 0 } });
    this.combineMat = pass(COMBINE_FRAG, { sScreen: { value: null }, sBloom: { value: null } });
    this.fxaaMat = pass(FXAA_FRAG, { texture0: { value: null }, rcpFrame: { value: new THREE.Vector2(1, 1) } });
    this.effectsMat = pass(EFFECTS_FRAG, {
      // THREE.Vector4 defaults w to 1: the effect modes and colours start at 0 (no effect).
      sScreen: { value: null }, sVignette: { value: null }, fSepiaColour: { value: new THREE.Vector4(0, 0, 0, 0) },
      fTintColour: { value: new THREE.Vector4(0, 0, 0, 0) }, fVignetteAlpha: { value: 0 }, fEffectMode: { value: new THREE.Vector4(0, 0, 0, 0) },
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0], 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    this.quad = new THREE.Mesh(g, this.copyMat);
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
  }

  /** The `Vignette` bitmap of WorldSystem.bdl (alpha = darkening at full intensity). */
  setVignette(tex: THREE.Texture): void {
    this.effectsMat.uniforms.sVignette.value = tex;
  }

  resize(w: number, h: number): void {
    if (w === this.width && h === this.height) return;
    this.width = w;
    this.height = h;
    const hw = Math.max(1, Math.floor(w / 2)), hh = Math.max(1, Math.floor(h / 2));
    for (const t of [this.bg, this.blurV, this.blurH, this.reflection]) t.setSize(hw, hh);
    for (const t of [this.main, this.aaInput, this.background]) t.setSize(w, h);
    (this.fxaaMat.uniforms.rcpFrame.value as THREE.Vector2).set(1 / w, 1 / h);
    // `PostProcessor::Update`: uScreenRes is the screen size; the blur shaders halve it.
    (this.blurVMat.uniforms.uScreenRes.value as THREE.Vector4).set(w, h, 0, 0);
  }

  private draw(gl: THREE.WebGLRenderer, mat: THREE.RawShaderMaterial, dst: Target | null): void {
    this.quad.material = mat;
    gl.setRenderTarget(dst);
    gl.render(this.scene, this.camera);
  }

  /** `Pass_PP_BlurV` + `Pass_PP_BlurH` over `src` (into `blurV`, then `blurH`). */
  blur(gl: THREE.WebGLRenderer, src: Target, size: number): void {
    this.blurVMat.uniforms.Screen.value = src.texture;
    this.blurVMat.uniforms.uBlurSize.value = size;
    this.draw(gl, this.blurVMat, this.blurV);
    this.blurHMat.uniforms.Screen.value = this.blurV.texture;
    this.blurHMat.uniforms.uBlurSize.value = size;
    this.draw(gl, this.blurHMat, this.blurH);
  }

  /** A full-surface textured quad of `src` into `dst` (no blending). */
  copy(gl: THREE.WebGLRenderer, src: THREE.Texture, dst: Target | null): void {
    this.copyMat.uniforms.Screen.value = src;
    this.draw(gl, this.copyMat, dst);
  }

  /** `AAPass:AALayer` (`Root.Effects.FXAAF` of `AASurfaceInputCol`) into the main colour
   * (`DRC_Final`). */
  antiAlias(gl: THREE.WebGLRenderer): void {
    this.fxaaMat.uniforms.texture0.value = this.aaInput.texture;
    this.draw(gl, this.fxaaMat, this.main);
  }

  /** The bloom passes over `main` and the effects pass onto the screen with `PostProcessor`'s
   * current effect values. */
  finish(gl: THREE.WebGLRenderer, fx: EffectUniforms): void {
    // PP_Bloom::Animate's values (the theme's BloomDefaults outside a Bloom flash, postfx.ts).
    this.lumMat.uniforms.sScreen.value = this.main.texture;
    this.lumMat.uniforms.lumThreshold.value = fx.bloomThreshold;
    this.lumMat.uniforms.lumMulti.value = fx.bloomIntensity;
    this.draw(gl, this.lumMat, this.blurH);
    // BloomBlurV reads DRS_BlurH into DRC_BlurV, BloomBlurH reads DRS_BlurV into DRC_BlurH.
    this.blur(gl, this.blurH, fx.bloomBlurSize);
    this.combineMat.uniforms.sScreen.value = this.main.texture;
    this.combineMat.uniforms.sBloom.value = this.blurH.texture;
    this.draw(gl, this.combineMat, this.background);
    const u = this.effectsMat.uniforms;
    u.sScreen.value = this.background.texture;
    (u.fEffectMode.value as THREE.Vector4).fromArray(fx.effectMode);
    (u.fSepiaColour.value as THREE.Vector4).fromArray(fx.sepiaColour);
    (u.fTintColour.value as THREE.Vector4).fromArray(fx.tintColour);
    u.fVignetteAlpha.value = u.sVignette.value ? fx.vignette : 0;
    this.draw(gl, this.effectsMat, null);
  }
}
